import { memo, lazy, Suspense } from 'react'
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown'
import remarkGfm from 'remark-gfm'
import ReactMarkdown, { type Components } from 'react-markdown'
import { CogoJsonPreview } from './cogo-json-preview'
import { ValidationResultMessage } from './validation-result-message'
import { ApprovalCard } from './approval-card'
import type { CompositeAction, CompositeActionItem } from '../services/composite-action'
import { commandBus } from '../services/command-bus'

const PageStructureCard = lazy(() => import('./page-structure-card').then(m => ({ default: m.PageStructureCard })))
const BlockPreviewCard = lazy(() => import('./block-preview-card').then(m => ({ default: m.BlockPreviewCard })))
const FlowSummaryCard = lazy(() => import('./flow-summary-card').then(m => ({ default: m.FlowSummaryCard })))
const ExpressionCardHost = lazy(() => import('@/features/expression-lab/components/expression-card-host').then(m => ({ default: m.ExpressionCardHost })))

const markdownComponents: Components = {
  code: ({ className, children, ...props }) => {
    const cls = String(className || '')

    // Detect ```cogo-json fenced code blocks
    if (/language-cogo-json/.test(cls)) {
      const jsonStr = String(children).replace(/\n$/, '')
      return <CogoJsonPreview json={jsonStr} />
    }

    // Detect ```cogo-validation fenced code blocks
    if (/language-cogo-validation/.test(cls)) {
      const textContent = String(children).replace(/\n$/, '')
      try {
        const validationData = JSON.parse(textContent)
        return <ValidationResultMessage data={validationData} />
      } catch {
        // Fall through to default code rendering
      }
    }

    // Detect ```cogo-composite fenced code blocks -> ApprovalCard
    if (/language-cogo-composite/.test(cls)) {
      const textContent = String(children).replace(/\n$/, '')
      try {
        const data = JSON.parse(textContent) as { actions: CompositeAction[] }
        return (
          <ApprovalCard
            compositeActions={data.actions}
            onApply={(selectedItems: CompositeActionItem[]) => {
              // D-07: decompose only the selected items (from Apply All or Edit flow)
              const allActions = selectedItems.flatMap(item => item.actions)
              commandBus.dispatchAll(allActions)
            }}
            onDismiss={() => {
              // No-op: just let the card show "Dismissed" status
            }}
          />
        )
      } catch {
        // Fall through to default code rendering
      }
    }

    // Detect ```cogo-page-structure fenced code blocks -> PageStructureCard
    if (/language-cogo-page-structure/.test(cls)) {
      const jsonStr = String(children).replace(/\n$/, '')
      return (
        <Suspense fallback={<div className="animate-pulse h-20 bg-muted rounded-lg" />}>
          <PageStructureCard json={jsonStr} />
        </Suspense>
      )
    }

    // Detect ```cogo-block-preview fenced code blocks -> BlockPreviewCard
    if (/language-cogo-block-preview/.test(cls)) {
      const jsonStr = String(children).replace(/\n$/, '')
      return (
        <Suspense fallback={<div className="animate-pulse h-20 bg-muted rounded-lg" />}>
          <BlockPreviewCard json={jsonStr} />
        </Suspense>
      )
    }

    // Detect ```cogo-flow-summary fenced code blocks -> FlowSummaryCard
    if (/language-cogo-flow-summary/.test(cls)) {
      const jsonStr = String(children).replace(/\n$/, '')
      return (
        <Suspense fallback={<div className="animate-pulse h-20 bg-muted rounded-lg" />}>
          <FlowSummaryCard json={jsonStr} />
        </Suspense>
      )
    }

    // Detect ```cogo-expression fenced code blocks -> ExpressionCardHost
    if (/language-cogo-expression/.test(cls)) {
      const jsonStr = String(children).replace(/\n$/, '')
      return (
        <Suspense fallback={<div className="animate-pulse h-20 bg-muted rounded-lg" />}>
          <ExpressionCardHost json={jsonStr} />
        </Suspense>
      )
    }

    // Fallback: detect plain ```json blocks that look like a BlockDef (has ui.root)
    if (/language-json/.test(cls)) {
      const text = String(children).replace(/\n$/, '')
      try {
        const obj = JSON.parse(text)
        if (obj?.ui?.root) {
          return <CogoJsonPreview json={text} />
        }
      } catch {
        /* not valid JSON, fall through to default code rendering */
      }
    }

    return <code className={cls || undefined} {...props}>{children as React.ReactNode}</code>
  },
}

// Durable replies are data restored from browser storage, not a fresh,
// admitted assistant turn. Keep ordinary Markdown/GFM presentation while
// refusing every custom fenced-card interpretation (notably cogo-composite,
// whose live renderer dispatches project mutations).
const persistedMarkdownComponents: Components = {
  code: ({ className, children, ...props }) => (
    <code className={className} {...props}>
      {children as React.ReactNode}
    </code>
  ),
}

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={markdownComponents}
      smooth
    />
  )
}

export const MarkdownText = memo(MarkdownTextImpl)

/** Static projection for assistant replies restored from the project thread. */
const PersistedMarkdownTextImpl = ({ text }: { text: string }) => (
  <div className="aui-md">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={persistedMarkdownComponents}>
      {text}
    </ReactMarkdown>
  </div>
)

export const PersistedMarkdownText = memo(PersistedMarkdownTextImpl)
